import { ArticleUtils } from '@article/util/article-utils';
import { ArticleNumberLong } from '@project/model/article-number-long';
import { Log } from '@shared/debug/log';
import { ArticleConstants } from '@article/constants/article-constants';
import { Dictionary } from 'typescript-collections';
import { ArticleVO } from '@article/model/vo/article.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { lastValueFrom, Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ArticleDataService } from '../service/article-data.service';
import { EventDispatcher } from 'three';
import { ArrayUtils } from '@shared/util/array-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleAccessoryManager } from './article-accessory.manager';
import { Injectable } from '@angular/core';
import { IsProjectType } from '@project/model/is-project-type';

@Injectable({ providedIn: 'root' })
export class ArticleManager extends EventDispatcher<Record<string, {}>> {
  public static readonly QUICK_LOAD_TYPE_LOAD_BY_ARTICLE_ID: number = 1;
  public static readonly QUICK_LOAD_TYPE_LOAD_BY_ARTICLE_NO: number = 2;
  public static readonly QUICK_LOAD_TYPE_LOAD_BY_ARTICLE_TYPE: number = 3;

  private _articleIdToVersionDictionary: Dictionary<number, Dictionary<number, ArticleVO>>;

  constructor(
    private articleDataService: ArticleDataService,
    private projectModel: ProjectModel,
    private articleAccessoryManager: ArticleAccessoryManager
  ) {
    super();
    this._articleIdToVersionDictionary = new Dictionary<number, Dictionary<number, ArticleVO>>();
  }

  public isArticleCached(articleId: number, versionId: number = -1): boolean {
    const versionDictionary = this._articleIdToVersionDictionary.getValue(articleId);
    if (!versionDictionary) {
      return false;
    }
    return versionDictionary.containsKey(versionId);
  }

  public getCachedArticle(articleId: number, versionId: number = -1): ArticleVO | undefined {
    const versionDictionary = this._articleIdToVersionDictionary.getValue(articleId);
    if (!versionDictionary) {
      return undefined;
    }
   return versionDictionary.getValue(versionId);
  }

  /** gets article from the cache or loads and adds it to the cache
   * @param name can be an articleNo (like "2002-1201") or a numerical articleId
   * @param version defaults to -1, which loads the latest version available */
  public getArticleByIdOrArticleNumberAndVersion(name: string, version: number = -1): Observable<ArticleVO | undefined> {
    return ArticleUtils.isStringArticleId(name)
      ? this.getArticleById(+name, version)
      : this.getArticleByExactArticleNo(name, version);
  }

  /** gets article from the cache or loads and adds it to the cache */
  public getArticleById(articleId: number, versionId: number = -1): Observable<ArticleVO | undefined> {
    return this.loadArticle(articleId, versionId);
  }

  /** gets article from the cache or loads and adds it to the cache */
  public getArticleByArticleNo(articleNo: string, version: number = -1): Observable<ArticleVO | undefined> {
    if (!articleNo) {
      return of(undefined);
    }

    const extendedArticleNumberLong = new ArticleNumberLong(articleNo).reducedArticleNumberLong;

    const search: ArticleVO[] = this.searchArticlesByArticleNumbers([extendedArticleNumberLong]);
    if(search.length > 0) {
      return of(search[0]);
    }

    return this.getArticleByExactArticleNo(extendedArticleNumberLong, version)
  }

  /** gets article from the cache or loads and adds it to the cache. Doesn't use extended or reduced articleNo for the search but the raw searchedArticleNo string */
  public getArticleByExactArticleNo(searchedArticleNo: string, version: number = -1) {
    const search: ArticleVO[] = this.searchArticlesByArticleNumbers([searchedArticleNo]);
    if(search.length > 0) {
      return of(search[0]);
    }

    return this.articleDataService.loadArticleByArticleNumber(searchedArticleNo, version)
      .pipe(tap(loadedArticle => this.addArticleToCache(loadedArticle)));
  }

  public getCachedArticleByArticleNo(articleNo: string): ArticleVO | undefined {
    if (!articleNo) {
      return undefined;
    }
    const search: ArticleVO[] = this.searchArticlesByArticleNumbers([articleNo]);
    return search.length > 0 ? search[0] : undefined;
  }

  public getArticlesByArticleNumbers(articleNumbers: string[]): ArticleVO[] {
    return this.searchArticlesByArticleNumbers(articleNumbers);
  }

  private searchArticlesByArticleNumbers(articleNumbers: string[]): ArticleVO[] {
    const result: ArticleVO[] = [];

    this._articleIdToVersionDictionary.forEach((articleId: number, versionDictionary: Dictionary<number, ArticleVO>) => {
      let maxVersion: number = -1;
      versionDictionary.forEach((version: number, article: ArticleVO) => {
        if (articleNumbers.indexOf(article.articleNo) !== -1 && version > maxVersion) {
          maxVersion = version;
        }
      });
      if (maxVersion > -1) {
        result.push(versionDictionary.getValue(maxVersion)!);
      }
    });
    return result;
  }

  /** Finds the basearticle of the current mcsGds or ltl project and sets it in the projects infoProperties */
  public async findBaseArticle(): Promise<ArticleVO | undefined> {
    const currentProject = this.projectModel?.currentProject;
    if (!currentProject || !IsProjectType.withBaseArticle(currentProject)) return undefined;

    if(IsProjectType.ltl(currentProject)) {
      const fakeBaseArticle = new ArticleVO();
      fakeBaseArticle.articleNo = ArticleConstants.LTL_DEFAULT_BASEARTICLE_NO;
      return fakeBaseArticle;
    }

    // Check if article is already loaded. If so, return that.
    const baseArticleNumber: string = ProjectUtils.getBaseArticleNumber(currentProject) ?? '';
    if (!StringUtils.isEmpty(baseArticleNumber) && this.existsArticleByArticleNumber(baseArticleNumber)) {
      return this.getCachedArticleByArticleNo(baseArticleNumber);
    }

    // Get the property values
    const mcsGdsInitArticleId: number = currentProject.infoProperties.getInt("mcsGdsInitArticleId");
    let mcsGdsInitArticleVersion: number = currentProject.infoProperties.getInt("mcsGdsInitArticleVersion");

    if (isNaN(mcsGdsInitArticleId)) {
      return undefined;
    }

    if (this.isArticleCached(mcsGdsInitArticleId, mcsGdsInitArticleVersion)) {
      return this.getCachedArticle(mcsGdsInitArticleId, mcsGdsInitArticleVersion);
    }

    if (isNaN(mcsGdsInitArticleVersion)) {
      mcsGdsInitArticleVersion = -1;
    }

    // Load the article
    const article = await lastValueFrom(this.loadFullArticle(mcsGdsInitArticleId, mcsGdsInitArticleVersion));

    if(Log.assertExists(article)) {
      currentProject.infoProperties.setString("mcsGdsInitArticleNo", article.articleNo)
    }

    return article ? article : undefined;
  }

  public addArticleToCache(article: ArticleVO): void {
    const addedArticle: boolean = this.internalAddArticleToCache(article, false);
    if (addedArticle) {
      this.dispatchEvent({ type: 'change' });
    }
  }

  /** Tries to add an article to the cache and returns true on success */
  private internalAddArticleToCache(article: ArticleVO, isLatestVersion: boolean): boolean {
    if (!article) {
      return false;
    }
    const articleId: number = article.identity;
    let versionDictionary = this._articleIdToVersionDictionary.getValue(articleId);
    if (!versionDictionary) {
      versionDictionary = new Dictionary<number, ArticleVO>();
      this._articleIdToVersionDictionary.setValue(articleId, versionDictionary);
    }
    let addedArticle: boolean = false;
    if (!versionDictionary.containsKey(article.version)) {
      versionDictionary.setValue(article.version, article);
      addedArticle = true;
    }
    if (isLatestVersion) {
      if (!versionDictionary.containsKey(-1)) {
        versionDictionary.setValue(-1, article);
        addedArticle = true;
      }
    }
    return addedArticle;
  }

  public clearArticleCache(): void {
    this._articleIdToVersionDictionary.clear();
  }

  public newLoadArticle_handler(articleId: number, articleVersion: number): Observable<ArticleVO | undefined> {
    if (!NumberUtils.isNumber(articleVersion)) {
      articleVersion = -1;
    }
    return this.loadFullArticle(articleId, articleVersion);
  }

  public loadFullArticle(articleId: number, articleVersion: number = -1): Observable<ArticleVO | undefined> {
    if (!NumberUtils.isNumber(articleId) || !NumberUtils.isNumber(articleVersion)) {
      return of(undefined);
    }

    return this.articleAccessoryManager.loadAccessoryInfoIfNotExists(articleId).pipe(
      () => this.loadArticle(articleId, articleVersion),
      () => this.loadArticle(articleId, articleVersion)
    );
  }

  private loadArticle(articleId: number, articleVersion: number = -1): Observable<ArticleVO | undefined> {
    const cachedArticle = this.getCachedArticle(articleId, articleVersion);
    if (cachedArticle) {
      return of(cachedArticle);
    } else {
      return this.articleDataService.loadArticleByArticleId(articleId, articleVersion).pipe(
        map((loadedArticle: ArticleVO) => {
          this.internalAddArticleToCache(loadedArticle, articleVersion === -1);
          return loadedArticle ? loadedArticle : undefined;
        }));
    }
  }

  public async addArticlesToCache(articles: ArticleVO[]): Promise<void> {
    await this.articleAccessoryManager.checkAccessoryInfoByArticles(articles).toPromise();
    this.internalAddArticlesToCache(articles, false);
  }

  private internalAddArticlesToCache(articles: ArticleVO[], isLatestVersion: boolean): void {
    let addedArticle: boolean = false;
    for (const article of articles) {
      if (article) {
        const oneAddedArticle: boolean = this.internalAddArticleToCache(article, isLatestVersion);
        if (oneAddedArticle) {
          addedArticle = true;
        }
      }
    }
    if (addedArticle) {
      this.dispatchEvent({ type: 'change' });
    }
  }

  public async loadArticlesByHeads(articleHeadsToLoad: ArticleVO[]): Promise<ArticleVO[]> {
    const articleHeads: ArticleVO[] = articleHeadsToLoad;
    const cachedArticles: ArticleVO[] = [];
    const headsForRequest: ArticleVO[] = [];
    for (const head of articleHeads) {
      const fullArticle = this.getCachedArticle(head.identity, head.version);
      if (!fullArticle) {
        headsForRequest.push(head);
      } else {
        cachedArticles.push(fullArticle);
      }
    }

    if (headsForRequest.length === 0) {
      return headsForRequest;
    }

    const articles = await lastValueFrom(this.articleDataService.getArticlesByHeads(headsForRequest));
    if (articles) {
      this.internalAddArticlesToCache(articles, false);
      return articles;
    } else {
      throw new Error("Heads could not be requested successful.");
    }
  }

  public addArticlesAsLatestVersion(articles: ArticleVO[]) {
    this.internalAddArticlesToCache(articles, true);
  }

  public async loadArticlesByArticleNumbers(articleNumbers: string[]): Promise<ArticleVO[]> {
    const articleNumbersToRetrieve: string[] = [];
    for (const articleNo of articleNumbers) {
      if (!this.existsArticleByArticleNumber(articleNo)) {
          articleNumbersToRetrieve.push(articleNo);
        }
    }

    const articles: ArticleVO[] = await lastValueFrom(this.articleDataService.getLatestArticlesByNoObservable(articleNumbersToRetrieve));
    await this.addArticlesToCache(articles);
    return this.getArticlesByArticleNumbers(articleNumbers);
  }

  public existsArticleByArticleNumber(articleNo: string): boolean {
    return this.searchArticlesByArticleNumbers([articleNo]).length > 0;
  }

  public getArticles(articleIds: number[]): Observable<ArticleVO[]> {
    if (ArrayUtils.isEmpty(articleIds)) {
      return of([]);
    }
    const articleIdsToRetrieve: number[] = [];
    const result: (ArticleVO | undefined)[] = [];
    for (let i = 0; i < articleIds.length; i++) {
      const articleId: number = articleIds[i];
      const article = this.getCachedArticle(articleId);
      result[i] = article;
      if (!article) {
        articleIdsToRetrieve.push(articleId);
      }
    }
    if (ArrayUtils.isEmpty(articleIdsToRetrieve)) {
      return of(result.filter((article) => article !== undefined));
    }
    const subject = new Subject<ArticleVO[]>();
    this.retrieveArticles(articleIdsToRetrieve).subscribe(() => {
      for (let i = 0; i < articleIds.length; i++) {
        const articleId: number = articleIds[i];
        result[i] = this.getCachedArticle(articleId);
      }
      subject.next(result as ArticleVO[]);
      subject.complete();
    });
    return subject;
  }

  private retrieveArticles(articleIds: number[]): Observable<ArticleVO[] | undefined> {
    const articleIdsToRetrieve: number[] = [];
    for (const articleId of articleIds) {
      if (!this.getCachedArticle(articleId)) {
        articleIdsToRetrieve.push(articleId);
      }
    }
    if (articleIdsToRetrieve.length > 0) {
      return this.articleDataService.getLatestArticles(articleIdsToRetrieve)
        .pipe(tap((articleVOs: ArticleVO[]) => {
        this.addArticlesAsLatestVersion(articleVOs);
        this.articleAccessoryManager.getLatestArticles_successHandler(articleVOs);
        this.articleDataService.notifyGetLatestArticles(true, articleVOs);
        }));
    } else {
      return of(undefined);
    }
  }
}
